import { Component, OnInit } from '@angular/core';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, Router } from '@angular/router';
import { QuoteAcknowledgement } from '../../shared/models/quote.acknowledgement.interface';
import { AreaSummary } from '../../quote/models/area.summary.interface';
import { QuoteSummary } from '../../quote/models/quote.summary.interface';
import { WeblinkService } from '../services/weblink.service';
import { Quote } from '../../shared/models/quote.interface';
import { QuoteArea } from '../../shared/models/quote.area.interface';
import { finalize } from 'rxjs';
import { ChangeOrder } from '../../shared/models/change.order.interface';
import { NgForm } from '@angular/forms';
import { QuoteAreaOptionGroup } from '../../shared/models/quoteAreaOptionGroup.interface';

@Component({
  selector: 'app-acknowledge',
  templateUrl: './acknowledge.component.html',
  styleUrls: ['./acknowledge.component.scss']
})
export class AcknowledgeComponent extends AbstractBiditComponent implements OnInit {

  hash: string;
  showQuote: boolean = false;
  acknowledgement: QuoteAcknowledgement;
  upgradeAreaSummaries: AreaSummary[] = new Array();
  quoteSummary: QuoteSummary = {} as QuoteSummary;
  quote: Quote = {} as Quote;
  changeOrder?: ChangeOrder;
  quoteRejected: boolean = false;
  quoteAccepted: boolean = false;
  dealerLogo: any;
  dealerLogo2: any;

  isLogoLoading: boolean = true;
  showMessage: boolean = false;
  acknowledgeementSignature: string = null;
  isSignatureMissing: boolean = false;
  allowAccept: boolean = false;
  isRequesting: boolean = false

  constructor(deviceDetectorService: DeviceDetectorService,
    private weblinkService: WeblinkService, private router: Router, private route: ActivatedRoute) {
    super(deviceDetectorService);
  }

  ngOnInit(): void {
    this.isLogoLoading = true;

    this.route.paramMap.subscribe(params => {
      this.hash = params.get('ackHash');
      if (this.hash != null) {
        this.weblinkService.getQuoteAcknowledgement(this.hash)
          .subscribe({
            next: (result) => {
              this.acknowledgement = result;
              this.loadDealerLogo(result.dealer_ID);
              if (!this.acknowledgement.isExpired && !this.acknowledgement.acknowledgedDate) {
                this.getQuoteData();
              }
              else {
                this.showMessage = true;
              }
            },
            error: (err) => {
              this.showMessage = true;
              /*this.onError(err);*/
            }
          });
      }
    });

  }

  getQuoteData() {
    if (this.acknowledgement?.quote_ID) {
      this.weblinkService.getQuote(this.acknowledgement.quoteAckHash)
        .subscribe({
          next: (result) => {
            this.quoteSummary = result;
            this.changeOrder = this.quoteSummary.quote.changeOrders.find(co => co.changeOrder_ID == this.acknowledgement.changeOrder_ID);

            this.quote = this.quoteSummary.quote;
            this.upgradeAreaSummaries = this.calculateUpgradeAreas();
            this.showQuote = true;
          },
          error: (err) => {
            this.showMessage = true;
            this.onError(err);
          }
        });
    };
  }

  rejectQuote(): void {
    var proceed = confirm($localize`Are you sure?`);
    if (!proceed) return;

    this.isRequesting = true;

    this.weblinkService.rejectQuote(this.acknowledgement.quoteAckHash)
      .subscribe({
        next: (result) => {
          this.quoteRejected = true;
          this.showQuote = false;
          this.showMessage = true;
          this.isRequesting = false;
        },
        error: (err) => {
          this.showMessage = true;
          this.isRequesting = false;
        }
      });
  }

  acceptQuote(form: NgForm): void {

    this.errors = '';
    if (this.acknowledgeementSignature == null) {
      this.isSignatureMissing = true;
      return;
    }

    this.isRequesting = true;

    let acceptedAreas: number[] = [];
    this.upgradeAreaSummaries.filter(uas => !uas.area_IsOption).forEach(a => {
      var selections = a.quoteAreas.map((qa: QuoteArea): number => {
        return qa.area_RadioValue;
      });
      acceptedAreas = acceptedAreas.concat(selections);
    });

    var optionSelections = [];
    this.upgradeAreaSummaries.filter(qas => qas.area_IsOption).forEach(a => {
      a.quoteAreaOptionGroups.forEach(og => {
        og.quoteAreaItems.filter(qai => qai.area_IsAccepted).forEach(
          i => { optionSelections.push(i.quoteAreaItem_ID) });
      });
    });

    acceptedAreas = acceptedAreas.concat(optionSelections);

    this.weblinkService.acceptQuote(this.acknowledgement.quoteAckHash, acceptedAreas, this.acknowledgeementSignature)
      .pipe(finalize(() => this.isRequesting = false))
      .subscribe(
        {
          next: (result) => {
            if (result) {
              this.quoteAccepted = true;
              this.showQuote = false;
              this.showMessage = true;
              this.isRequesting = false;
            }
          },
          error: (error: string) => { this.errors = error; this.showMessage = true; this.isRequesting = false; }
        });
  }

  calculateUpgradeAreas(): AreaSummary[] {
    if (this.changeOrder != undefined)
      return this.calculateChangeOrderAreas();

    this.quote.optionCost = 0;
    this.quote.totalCost = 0;

    let quoteMargin = (100 - this.quote.quote_GrossMargin) / 100;

    // change order area summaries should be removed
    this.quoteSummary.areaSummaries = this.quoteSummary.areaSummaries.filter(as =>
      as.quoteAreas.length > 0 && as.quoteAreas[0].quoteItem != null);

    this.quoteSummary.areaSummaries.forEach(a => {
      a.quoteAreas.forEach(qa => {
        if (a.area_IsOption) {
          qa.area_RadioValue = 0;
        }
        else {
          qa.area_RadioValue = qa.quoteItem.quoteAreaItem_ID;
          this.quoteSummary.quote.totalCost += qa.quoteItem.area_TotalCost;
        }

        qa.quoteUpgrades.forEach(u => {
          u.upgradeBid = (u.area_TotalCost / quoteMargin) - (qa.quoteItem.area_TotalCost / quoteMargin);
          u.upgradeDelta = (u.area_TotalCost - qa.quoteItem.area_TotalCost);
        });
      })

      if (a.quoteAreaOptionGroups !== null) {
        a.quoteAreaOptionGroups.forEach(og => {
          og.quoteAreaItems.forEach(qai => {
            qai.area_IsAccepted = false;
          });

          if (og.optionGroup !== null) {
            og.group_radioValue = 0
            og.group_isAccepted = false;
          }
        })
      }
    });

    return this.quoteSummary.areaSummaries;
  }

  calculateChangeOrderAreas(): AreaSummary[] {
    this.quote.optionCost = 0;
    this.quote.totalCost = 0;

    let quoteMargin = (100 - this.changeOrder.changeOrder_GrossMargin) / 100;

    const areaSummaries: AreaSummary[] = [];
    this.quoteSummary.areaSummaries.forEach(as => {
      const cois = this.changeOrder.changeOrderItems.filter(coi => coi.area_ID == as.area_ID);
      if (cois.length == 0) return;

      as.quoteAreas = [];
      cois.forEach(coi => {
        let quoteArea = {
          quoteArea_ID: coi.changeOrderItem_ID,
          quoteItem: {
            quoteArea_ID: coi.changeOrderItem_ID,
            quoteAreaItem_ID: coi.changeOrderItem_ID,
            area_Name: coi.area.area_Name,
            item: coi.item,
            itemUsage: coi.itemUsage
          }
        } as QuoteArea;

        if (coi.changeOrderItem_Note && quoteArea?.quoteItem?.area_Name)
          quoteArea.quoteItem.area_Name += ` ${coi.changeOrderItem_Note}`;

        if (coi.area.area_IsOption) {
          quoteArea.area_RadioValue = 0;
        }
        else {
          quoteArea.area_RadioValue = coi.changeOrderItem_ID;
          this.quoteSummary.quote.totalCost += coi.changeOrderItem_TotalCost;
        }

        as.quoteAreas.push(quoteArea);
      })

      areaSummaries.push(as);
    });

    return areaSummaries;
  }

  calculateConversion() {
    this.calculateProjectCost();
    let marginRatio = (100 - (this.quote.quote_GrossMargin ?? 0)) / 100;

    let productAmount = this.quote.totalCost / marginRatio;
    this.quoteSummary.quote.quote_BidAmount = productAmount + this.quoteSummary.quote.optionCost;
    this.quoteSummary.quote.quote_BidAmount = Math.ceil(this.quoteSummary.quote.quote_BidAmount);
  }

  calculateProjectCost() {
    this.quote.totalCost = 0;
    this.quote.optionCost = 0;

    this.upgradeAreaSummaries.forEach(a => {
      a.quoteAreas.forEach(qa => {
        let qai = qa.quoteAreaItems.find(qai => qai.quoteAreaItem_ID == qa.area_RadioValue);
        if (qai != null) {
          this.quote.totalCost += (qa.area.area_IsOption ? 0 : qai.area_TotalCost);
          this.quote.optionCost += (qa.area.area_IsOption ? qa.quoteItem.optionBid : 0);
        }
      });
    });

    this.calculateOptionsTotal();
  }

  setOptionGroupSelected(selectedOptionGroup: number, selected: number) {
    this.upgradeAreaSummaries.filter(qas => qas.area_IsOption).forEach(a => {
      let optionGroup = a.quoteAreaOptionGroups.find(qag => qag.optionGroup === selectedOptionGroup) as QuoteAreaOptionGroup

      optionGroup.group_isAccepted = selected != 0;

      optionGroup.quoteAreaItems.forEach(qai => {
        qai.area_IsAccepted = selected != 0;
      });
    });

    this.calculateConversion();
  }

  setOptionSelected(quoteAreaItemId: number, isSelected: boolean) {
    this.upgradeAreaSummaries.filter(qas => qas.area_IsOption).forEach(a => {
      let optionGroup = a.quoteAreaOptionGroups.find(qag => qag.optionGroup === null) as QuoteAreaOptionGroup

      optionGroup.quoteAreaItems.find(qai => qai.quoteAreaItem_ID === quoteAreaItemId).area_IsAccepted = isSelected;
    });

    this.calculateConversion();
  }

  calculateOptionsTotal() {
    let optionGroupTotal = 0;

    this.upgradeAreaSummaries.filter(qas => qas.area_IsOption).forEach(a => {

      a.quoteAreaOptionGroups.forEach(og => {
        if (og.group_isAccepted) {
          og.quoteAreaItems.forEach(qai => {
            optionGroupTotal += qai.optionBid;
          })
        }
      });

      let baseOptionGroup = a.quoteAreaOptionGroups.find(og => og.optionGroup == null);
      baseOptionGroup?.quoteAreaItems.filter(qai => qai.area_IsAccepted).forEach(qai => {
        optionGroupTotal += qai.optionBid;
      })
    });


    this.quote.optionCost += optionGroupTotal;
  }

  loadDealerLogo(dealerID: number): void {
    this.isLogoLoading = true;

    this.weblinkService.getDealerLogo(dealerID)
      .subscribe({
        next: (data) => {
          this.convertBase64FromBlob(data);
        },
        error: (error: string) => {
          this.isLogoLoading = false;
          this.errors = error;
        }
      });
  }

  convertBase64FromBlob(image: Blob): void {
    let reader = new FileReader();
    reader.addEventListener("load", () => {
      this.dealerLogo = reader.result;
      this.isLogoLoading = false;

    }, false);

    if (image) {
      reader.readAsDataURL(image);
    }
  }

  acknowlegementSigned(signature: string) {
    if (signature === null) {
      this.allowAccept = false;
    }
    else {
      this.acknowledgeementSignature = signature;
      this.isSignatureMissing = false;
      this.allowAccept = true;
    }
  }

  clearMessage() {
    this.isSignatureMissing = false;
  }
}
